import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import { RISK, STATUS_COLOR, fmtEur, fmtNum } from "@/lib/format";
import { exportElementToPdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { SimulationPanel } from "@/components/SimulationPanel";
import { MarginsBar, StructureDonuts, BenchmarkPanel, TrendCharts } from "@/components/ReportCharts";
import {
  Printer, RefreshCw, ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Sparkles, Layers, Waves, PieChart, BrainCircuit, GitBranch, ShieldAlert,
  Activity, Target, Compass, Download, BarChart3, LineChart,
} from "lucide-react";

function ScoreGauge({ score, color }) {
  const data = [{ value: score, fill: color }];
  return (
    <div className="relative h-40 w-40">
      <RadialBarChart width={160} height={160} innerRadius="72%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
        <RadialBar background={{ fill: "#F1F5F9" }} dataKey="value" cornerRadius={20} />
      </RadialBarChart>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-3xl font-extrabold text-white">{score}</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">/ 100</span>
      </div>
    </div>
  );
}

function pctDelta(cur, prev) {
  if (cur == null || prev == null || Math.abs(prev) < 1e-9) return null;
  return ((cur - prev) / Math.abs(prev)) * 100;
}

function Delta({ pct, size = "sm" }) {
  if (pct == null || !isFinite(pct)) return null;
  const up = pct >= 0;
  const cls = size === "lg" ? "text-xs" : "text-[10px]";
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${cls} ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function Row({ label, value, bold, accent, prev }) {
  const d = pctDelta(value, prev);
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 ${bold ? "bg-slate-900 font-semibold" : ""} ${accent ? "border-l-2 border-blue-500" : ""}`}>
      <span className={`text-sm ${bold ? "text-white" : "text-slate-300"}`}>{label}</span>
      <span className="flex items-center gap-2">
        {d != null && <Delta pct={d} />}
        <span className={`font-mono-fin text-sm ${bold ? "text-white" : "text-slate-200"}`}>{fmtEur(value)}</span>
      </span>
    </div>
  );
}

function StatementCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden print-break">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-900">
        <Icon className="h-4 w-4 text-blue-600" />
        <h3 className="font-heading text-sm font-semibold text-white">{title}</h3>
      </div>
      <div className="divide-y divide-slate-800">{children}</div>
    </div>
  );
}

function RatioCard({ item }) {
  const { t } = useI18n();
  const isNa = item.status === "na" || item.value === null;
  const s = isNa ? STATUS_COLOR.na : (STATUS_COLOR[item.status] || STATUS_COLOR.na);
  return (
    <div className={`rounded-lg border p-4 print-break ${isNa ? "border-slate-800 bg-slate-900/60" : "border-slate-800 bg-slate-900"}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-slate-100">{item.label}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{item.desc}</p>
        </div>
        <span className={`h-2.5 w-2.5 rounded-full mt-1 shrink-0 ${s.dot}`} />
      </div>
      {isNa ? (
        <div className="mt-3">
          <p className="font-mono-fin text-xl font-semibold text-slate-500">{t("ratio_na")}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{t("ratio_na_note")}</p>
        </div>
      ) : (
        <p className={`mt-3 font-mono-fin text-xl font-semibold ${s.text}`}>
          {fmtNum(item.value, item.unit)}
        </p>
      )}
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [regen, setRegen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    api.get(`/analyses/${id}`).then((r) => setData(r.data)).catch(() => {
      toast.error(lang === "it" ? "Analisi non trovata" : "Analysis not found");
      navigate("/dashboard");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll for AI commentary while it is being generated in the background
  useEffect(() => {
    if (!data || data.commentary_status !== "generating") return;
    const iv = setInterval(async () => {
      try {
        const { data: fresh } = await api.get(`/analyses/${id}`);
        if (fresh.commentary_status !== "generating") setData(fresh);
      } catch (e) {
        console.debug("Commentary polling retry after transient error:", e);
      }
    }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.commentary_status, id]);

  const regenerate = async () => {
    setRegen(true);
    try {
      await api.post(`/analyses/${id}/commentary`);
      setData((p) => ({ ...p, commentary_status: "generating" }));
      toast.success(lang === "it" ? "Rigenerazione avviata…" : "Regeneration started…");
    } catch {
      toast.error("Error");
    } finally { setRegen(false); }
  };

  const downloadPdf = async () => {
    if (!reportRef.current) return;
    if (data?.commentary_status === "generating") toast.info(t("pdf_wait_ai"));
    setPdfLoading(true);
    try {
      const safe = (data?.company_name || "report").replace(/[^\w-]+/g, "_").slice(0, 40);
      await exportElementToPdf(reportRef.current, `Rexxpons_${safe}_${data?.fiscal_year || ""}.pdf`);
    } catch (e) {
      toast.error(lang === "it" ? "Errore nella generazione del PDF" : "PDF generation error");
    } finally {
      setPdfLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-slate-800 border-t-blue-600 animate-spin" />
      </div>
    );
  }

  const res = data.result;
  const sp = res.reclassification.stato_patrimoniale;
  const ce = res.reclassification.conto_economico;
  const cf = res.cash_flow;
  const rating = res.rating;
  const priorSP = res.prior_reclassification?.stato_patrimoniale;
  const priorCE = res.prior_reclassification?.conto_economico;
  const com = data.commentary || {};
  const ew = data.early_warnings || [];
  const isGenerating = data.commentary_status === "generating" || (!data.commentary && data.commentary_status !== "error");
  const isError = data.commentary_status === "error";
  const r = RISK[rating.grade] || RISK.C;

  const EW_STYLE = {
    ok: { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: t("ew_ok") },
    watch: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", label: t("ew_watch") },
    alert: { dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", label: t("ew_alert") },
  };
  const CHAIN_STYLE = {
    positivo: "text-emerald-600 border-emerald-200 bg-emerald-50", positive: "text-emerald-600 border-emerald-200 bg-emerald-50",
    neutro: "text-slate-300 border-slate-800 bg-slate-900", neutral: "text-slate-300 border-slate-800 bg-slate-900",
    negativo: "text-red-600 border-red-200 bg-red-50", negative: "text-red-600 border-red-200 bg-red-50",
  };

  const ratioCats = [
    { key: "liquidity", label: t("cat_liquidity") },
    { key: "leverage", label: t("cat_leverage") },
    { key: "profitability", label: t("cat_profitability") },
    { key: "efficiency", label: t("cat_efficiency") },
  ];

  return (
    <div className="App bg-slate-900 min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="report-view" ref={reportRef}>
        {/* toolbar */}
        <div className="no-print flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("back")}
          </Button>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={regenerate} disabled={regen} data-testid="regen-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${regen ? "animate-spin" : ""}`} /> {t("regenerate")}
            </Button>
            <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfLoading} data-testid="pdf-btn">
              <Download className={`h-4 w-4 mr-1.5 ${pdfLoading ? "animate-pulse" : ""}`} /> {pdfLoading ? t("pdf_generating") : t("pdf")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/business-plan/new?from=${id}`)} data-testid="to-bp-btn">
              <LineChart className="h-4 w-4 mr-1.5" /> Business Plan
            </Button>
            <Button size="sm" onClick={() => window.print()} data-testid="print-btn">
              <Printer className="h-4 w-4 mr-1.5" /> {t("print")}
            </Button>
          </div>
        </div>

        {/* Header + rating */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-8 print-break">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">{t("report_title")}</p>
              <h1 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="report-company">{data.company_name}</h1>
              <p className="mt-1 text-slate-400">{t("fiscal_year")}: {data.fiscal_year}{data.sector ? ` · ${res.benchmark ? (lang === "it" ? res.benchmark.label_it : res.benchmark.label_en) : data.sector}` : ""}</p>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg">
                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">Ricavi</p><p className="font-mono-fin font-semibold text-white">{fmtEur(ce.valore_produzione)}</p><Delta pct={pctDelta(ce.valore_produzione, priorCE?.valore_produzione)} size="lg" /></div>
                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">EBITDA</p><p className="font-mono-fin font-semibold text-white">{fmtEur(ce.mol_ebitda)}</p><Delta pct={pctDelta(ce.mol_ebitda, priorCE?.mol_ebitda)} size="lg" /></div>
                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">EBIT</p><p className="font-mono-fin font-semibold text-white">{fmtEur(ce.ebit)}</p><Delta pct={pctDelta(ce.ebit, priorCE?.ebit)} size="lg" /></div>
                <div><p className="text-[11px] uppercase tracking-wider text-slate-500">Utile</p><p className="font-mono-fin font-semibold text-white">{fmtEur(ce.utile_netto)}</p><Delta pct={pctDelta(ce.utile_netto, priorCE?.utile_netto)} size="lg" /></div>
              </div>
            </div>

            <div className={`flex items-center gap-4 sm:gap-6 rounded-xl ${r.bg} ring-1 ${r.ring} p-5 sm:p-6`} data-testid="rating-block">
              <ScoreGauge score={rating.score} color={r.color} />
              <div>
                <div className={`h-16 w-16 rounded-xl ${r.solid} flex items-center justify-center`}>
                  <span className="font-heading text-4xl font-extrabold text-white" data-testid="rating-grade">{rating.grade}</span>
                </div>
                <p className={`mt-3 text-sm font-semibold ${r.text} max-w-[160px]`}>{rating.band}</p>
                <p className="text-xs text-slate-400 mt-1">{t("pd_label")}: {rating.pd_estimate}</p>
              </div>
            </div>
          </div>

          {/* rating components */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-5 gap-3">
            {rating.components.map((c) => {
              const col = c.score >= 65 ? "#10B981" : c.score >= 45 ? "#F59E0B" : "#EF4444";
              return (
                <div key={c.key} className="rounded-lg bg-slate-900 border border-slate-800 p-3">
                  <p className="text-xs text-slate-400">{c.label}</p>
                  <p className="font-mono-fin text-lg font-semibold text-white">{c.score}</p>
                  <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${c.score}%`, backgroundColor: col }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Early Warning */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-blue-600" /> {t("r_early")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="early-warnings">
          {ew.map((w, i) => {
            const st = EW_STYLE[w.level] || EW_STYLE.watch;
            return (
              <div key={`ew-${i}`} className="rounded-lg border border-slate-800 bg-slate-900 p-4 print-break">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-100">{w.indicator}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.chip}`}>{st.label}</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{w.message}</p>
              </div>
            );
          })}
        </div>

        {/* Reclassification */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <Layers className="h-5 w-5 text-blue-600" /> {t("r_reclass")}
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <StatementCard title={t("r_sp")} icon={Layers}>
            <Row label={t("cat_liquidity") + " — Immediate"} value={sp.liquidita_immediate} prev={priorSP?.liquidita_immediate} />
            <Row label="Liquidità differite" value={sp.liquidita_differite} prev={priorSP?.liquidita_differite} />
            <Row label="Rimanenze" value={sp.rimanenze} prev={priorSP?.rimanenze} />
            <Row label="Attivo corrente" value={sp.attivo_corrente} bold prev={priorSP?.attivo_corrente} />
            <Row label="Attivo fisso" value={sp.attivo_fisso} bold prev={priorSP?.attivo_fisso} />
            <Row label="TOTALE ATTIVO" value={sp.totale_attivo} bold accent prev={priorSP?.totale_attivo} />
            <Row label="Patrimonio netto" value={sp.patrimonio_netto} bold prev={priorSP?.patrimonio_netto} />
            <Row label="Passivo consolidato" value={sp.passivo_consolidato} bold prev={priorSP?.passivo_consolidato} />
            <Row label="Passivo corrente" value={sp.passivo_corrente} bold prev={priorSP?.passivo_corrente} />
            <Row label="TOTALE PASSIVO" value={sp.totale_passivo} bold accent prev={priorSP?.totale_passivo} />
            <Row label="Capitale Circolante Netto" value={sp.capitale_circolante_netto} prev={priorSP?.capitale_circolante_netto} />
            <Row label="Posizione Finanziaria Netta" value={sp.posizione_finanziaria_netta} prev={priorSP?.posizione_finanziaria_netta} />
          </StatementCard>

          <StatementCard title={t("r_ce")} icon={PieChart}>
            <Row label="Valore della Produzione" value={ce.valore_produzione} bold prev={priorCE?.valore_produzione} />
            <Row label="Costi esterni" value={ce.costi_esterni} prev={priorCE?.costi_esterni} />
            <Row label="Valore Aggiunto" value={ce.valore_aggiunto} bold accent prev={priorCE?.valore_aggiunto} />
            <Row label="Costi del personale" value={ce.costi_personale} prev={priorCE?.costi_personale} />
            <Row label="MOL (EBITDA)" value={ce.mol_ebitda} bold accent prev={priorCE?.mol_ebitda} />
            <Row label="Ammortamenti" value={ce.ammortamenti} prev={priorCE?.ammortamenti} />
            <Row label="Accantonamenti" value={ce.accantonamenti} prev={priorCE?.accantonamenti} />
            <Row label="Reddito Operativo (EBIT)" value={ce.ebit} bold accent prev={priorCE?.ebit} />
            <Row label="Saldo gestione finanziaria" value={ce.saldo_finanziario} prev={priorCE?.saldo_finanziario} />
            <Row label="Risultato ante imposte" value={ce.risultato_ante_imposte} bold prev={priorCE?.risultato_ante_imposte} />
            <Row label="Imposte" value={ce.imposte} prev={priorCE?.imposte} />
            <Row label="UTILE NETTO" value={ce.utile_netto} bold accent prev={priorCE?.utile_netto} />
          </StatementCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <MarginsBar ce={ce} t={t} />
          <StructureDonuts sp={sp} t={t} />
        </div>

        {/* Multi-year trend */}
        {Array.isArray(res.trend) && res.trend.length >= 2 && (
          <>
            <h2 className="font-heading text-xl font-bold text-white mt-10 mb-1 flex items-center gap-2">
              <LineChart className="h-5 w-5 text-blue-600" /> {t("r_trend")}
            </h2>
            <p className="text-sm text-slate-400 mb-4">{t("trend_sub")}</p>
            <TrendCharts trend={res.trend} t={t} />
            <p className="text-xs text-slate-500 mt-3 italic" data-testid="trend-note">{t("trend_note")}</p>
          </>
        )}

        {/* Cash flow */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <Waves className="h-5 w-5 text-blue-600" /> {t("r_cash")}
        </h2>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 print-break">
          {!cf.has_prior && cf.note && (
            <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {cf.note}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { label: lang === "it" ? "Flusso Operativo" : "Operating CF", v: cf.flusso_operativo },
              { label: lang === "it" ? "Flusso Investimenti" : "Investing CF", v: cf.flusso_investimenti },
              { label: lang === "it" ? "Flusso Finanziamenti" : "Financing CF", v: cf.flusso_finanziamenti },
              { label: lang === "it" ? "Flusso di Cassa Netto" : "Net Cash Flow", v: cf.flusso_netto, accent: true },
            ].map((b) => (
              <div key={b.label} className={`rounded-lg p-4 border ${b.accent ? "bg-slate-900 border-slate-900" : "bg-slate-900 border-slate-800"}`}>
                <p className={`text-xs ${b.accent ? "text-slate-300" : "text-slate-400"}`}>{b.label}</p>
                <p className={`mt-1 font-mono-fin text-lg font-semibold flex items-center gap-1.5 ${b.accent ? "text-white" : (b.v >= 0 ? "text-emerald-600" : "text-red-600")}`}>
                  {b.v !== null && b.v !== undefined && (b.v >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />)}
                  {fmtEur(b.v)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Ratios */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <PieChart className="h-5 w-5 text-blue-600" /> {t("r_ratios")}
        </h2>
        {(ce.valore_produzione || 0) === 0 && (
          <div className="mb-4 rounded-xl border border-slate-800 bg-slate-900 p-4 flex items-start gap-3" data-testid="inactive-banner">
            <span className="mt-1 h-2.5 w-2.5 rounded-full bg-slate-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-slate-100">{t("inactive_title")}</p>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed max-w-3xl">{t("inactive_body")}</p>
            </div>
          </div>
        )}
        <div className="space-y-6">
          {ratioCats.map((cat) => (
            <div key={cat.key}>
              <p className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-3">{cat.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {res.ratios[cat.key].map((item) => <RatioCard key={item.key} item={item} />)}
              </div>
            </div>
          ))}
        </div>

        {/* Sector benchmark */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-600" /> {t("r_bench")}
        </h2>        <p className="text-sm text-slate-400 mb-4">{t("bench_sub")}</p>
        {res.benchmark ? (
          <BenchmarkPanel ratios={res.ratios} benchmark={res.benchmark} rating={rating} lang={lang} t={t} />
        ) : (
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 text-sm text-slate-400">{t("bench_none")}</div>
        )}

        {/* Simulation & Stress Test */}
        <h2 className="font-heading text-xl font-bold text-white mt-10 mb-1 flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" /> {t("sim_title")}
        </h2>
        <p className="text-sm text-slate-400 mb-4">{t("sim_sub")}</p>
        <SimulationPanel analysisId={id} />

        {/* Autonomous commentary */}
        <div className="mt-12 rounded-2xl bg-slate-900 text-white overflow-hidden relative print-break" data-testid="commentary-section">
          <div className="absolute inset-0 grain opacity-100" />
          <div className="relative p-6 sm:p-10">
            <div className="flex items-center gap-2 mb-1">
              <BrainCircuit className="h-5 w-5 text-blue-400" />
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-400">
                {t("r_comment")} {com._ai_generated && <Sparkles className="inline h-3 w-3 ml-1" />}
              </p>
            </div>
            <h2 className="font-heading text-2xl font-bold text-white">{t("r_summary")}</h2>

            {isGenerating ? (
              <div className="mt-6 flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 p-6" data-testid="commentary-generating">
                <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-blue-400 animate-spin shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-white">{t("commentary_generating")}</p>
                  <p className="text-sm text-slate-300 mt-1">{t("commentary_generating_sub")}</p>
                </div>
              </div>
            ) : isError ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl bg-red-500/10 border border-red-400/30 p-6" data-testid="commentary-error">
                <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
                <p className="text-sm text-red-200">{t("commentary_error")}</p>
              </div>
            ) : (
              <>
                {com.executive_diagnosis && (
                  <div className="mt-4 rounded-xl bg-blue-500/10 border border-blue-400/30 p-5" data-testid="executive-diagnosis">
                    <p className="text-xs font-semibold tracking-wider uppercase text-blue-300 mb-2">{t("r_diagnosis")}</p>
                    <p className="text-base text-white leading-relaxed">{com.executive_diagnosis}</p>
                  </div>
                )}
                <p className="mt-6 text-slate-200 leading-relaxed max-w-3xl" data-testid="commentary-summary">{com.executive_summary}</p>

                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="rounded-xl bg-white/5 border border-white/10 p-5">
                    <div className="flex items-center gap-2 text-emerald-400 mb-3"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-semibold">{t("r_strengths")}</span></div>
                    <ul className="space-y-2 text-sm text-slate-200">
                      {(com.strengths || []).map((s, i) => <li key={`str-${i}`} className="flex gap-2"><span className="text-emerald-400">•</span>{s}</li>)}
                    </ul>
                  </div>
                  <div className="rounded-xl bg-white/5 border border-white/10 p-5">
                    <div className="flex items-center gap-2 text-amber-400 mb-3"><AlertTriangle className="h-4 w-4" /><span className="text-sm font-semibold">{t("r_weaknesses")}</span></div>
                    <ul className="space-y-2 text-sm text-slate-200">
                      {(com.weaknesses || []).map((s, i) => <li key={`weak-${i}`} className="flex gap-2"><span className="text-amber-400">•</span>{s}</li>)}
                    </ul>
                  </div>
                  <div className="rounded-xl bg-white/5 border border-white/10 p-5">
                    <div className="flex items-center gap-2 text-red-400 mb-3"><TrendingDown className="h-4 w-4" /><span className="text-sm font-semibold">{t("r_drivers")}</span></div>
                    <ul className="space-y-2 text-sm text-slate-200">
                      {(com.risk_drivers || []).map((s, i) => <li key={`drv-${i}`} className="flex gap-2"><span className="text-red-400">•</span>{s}</li>)}
                    </ul>
                  </div>
                </div>

                {Array.isArray(com.causal_chain) && com.causal_chain.length > 0 && (
                  <div className="mt-8" data-testid="causal-chain">
                    <div className="flex items-center gap-2 text-blue-300 mb-3"><GitBranch className="h-4 w-4" /><span className="text-sm font-semibold">{t("r_causal")}</span></div>
                    <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
                      {com.causal_chain.map((node, i) => (
                        <React.Fragment key={`chain-${i}`}>
                          <div className={`flex-1 rounded-lg border p-3 ${CHAIN_STYLE[node.assessment] || CHAIN_STYLE.neutro}`}>
                            <p className="text-xs font-semibold">{node.stage}</p>
                            <p className="text-[11px] mt-1 opacity-80 leading-snug">{node.note}</p>
                          </div>
                          {i < com.causal_chain.length - 1 && (
                            <div className="hidden sm:flex items-center text-slate-400">→</div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-8">
                  <p className="text-sm font-semibold text-blue-300 mb-2">{t("r_rationale")}</p>
                  <p className="text-sm text-slate-200 leading-relaxed max-w-3xl">{com.rating_rationale}</p>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="operational-decisions">
                    <div className="flex items-center gap-2 text-blue-300 mb-3"><Target className="h-4 w-4" /><span className="text-sm font-semibold">{t("r_ops90")}</span></div>
                    <ul className="space-y-3 text-sm text-slate-200">
                      {(com.operational_decisions_90d || []).map((o, i) => (
                        <li key={`ops-${i}`} className="flex gap-2">
                          <span className="text-blue-300 shrink-0">{i + 1}.</span>
                          <span><span className="text-white">{o.action}</span>{o.impact && <span className="block text-[11px] text-emerald-300 mt-0.5">→ {o.impact}</span>}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid="strategic-decisions">
                    <div className="flex items-center gap-2 text-blue-300 mb-3"><Compass className="h-4 w-4" /><span className="text-sm font-semibold">{t("r_strat12")}</span></div>
                    <ul className="space-y-3 text-sm text-slate-200">
                      {(com.strategic_decisions_12m || []).map((o, i) => (
                        <li key={`strat-${i}`} className="flex gap-2">
                          <span className="text-blue-300 shrink-0">{i + 1}.</span>
                          <span><span className="text-white">{o.action}</span>{o.impact && <span className="block text-[11px] text-emerald-300 mt-0.5">→ {o.impact}</span>}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-8">
          Rexxpons · Rating & Analisi del Credito
        </p>
      </main>
    </div>
  );
}
